import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import {
  FaUserCircle, FaCrown, FaWallet, FaLock, FaEnvelope,
  FaCalendarAlt, FaLink, FaChevronRight, FaCheckCircle,
  FaSlidersH, FaHistory,
} from 'react-icons/fa';
import { getRequest, patchRequest } from '../../services/api/apiService';

const Section = ({ title, children }) => (
  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
    <h2 className="text-sm font-bold text-[#221C51] uppercase tracking-wide mb-5">{title}</h2>
    {children}
  </div>
);

const Profile = () => {
  const [user, setUser] = useState(null);
  const [billing, setBilling] = useState(null);
  const [name, setName] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  useEffect(() => {
    Promise.all([
      getRequest('/api/auth/user-info'),
      getRequest('/api/payment/status').catch(() => null),
    ]).then(([userData, billingData]) => {
      setUser(userData);
      setName(userData.name || '');
      setBilling(billingData);
    }).catch(() => toast.error('Failed to load profile.'));
  }, []);

  const handleSaveName = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error('Name cannot be empty.');
    setSavingProfile(true);
    try {
      await patchRequest('/api/auth/profile', { name: name.trim() });
      toast.success('Name updated.');
      setUser((u) => ({ ...u, name: name.trim() }));
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Something went wrong while updating.');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleSavePassword = async (e) => {
    e.preventDefault();
    if (!currentPassword) return toast.error('Current password required.');
    if (newPassword.length < 4) return toast.error('The new password must be at least 4 characters.');
    if (newPassword !== confirmPassword) return toast.error('Passwords do not match.');
    setSavingPassword(true);
    try {
      await patchRequest('/api/auth/profile', { currentPassword, newPassword });
      toast.success('Password updated.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Something went wrong while changing the password.');
    } finally {
      setSavingPassword(false);
    }
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center h-full py-20">
        <p className="text-gray-400 text-sm">Loading profile…</p>
      </div>
    );
  }

  const initials = user.name
    ? user.name.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2)
    : '?';

  const memberSince = user.created_at
    ? new Date(user.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    : '—';

  const isGoogle = !!user.google_id;
  const isPremium = billing?.isPremium || user.ispremium;
  const walletBalance = billing?.walletBalance ?? parseFloat(user.wallet_balance || 0).toFixed(2);

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-5">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* ── Header ── */}
      <div className="bg-[#221C51] rounded-2xl p-6 flex items-center gap-5 shadow">
        <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center text-white text-2xl font-bold flex-shrink-0">
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xl font-bold text-white truncate">{user.name}</p>
          <p className="text-sm text-white/70 flex items-center gap-1 truncate mt-0.5">
            <FaEnvelope className="flex-shrink-0" /> {user.email}
          </p>
          <p className="text-xs text-white/50 flex items-center gap-1 mt-1">
            <FaCalendarAlt className="flex-shrink-0" /> Member since {memberSince}
          </p>
        </div>
        <div className="flex flex-col items-end gap-2 flex-shrink-0">
          <span className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold ${isPremium ? 'bg-yellow-400 text-yellow-900' : 'bg-white/20 text-white'}`}>
            <FaCrown /> {isPremium ? 'Premium' : 'Free'}
          </span>
          <span className="flex items-center gap-1 px-3 py-1 bg-white/10 text-white rounded-full text-xs font-semibold">
            <FaWallet /> €{walletBalance}
          </span>
        </div>
      </div>

      {/* ── Account Settings ── */}
      <Section title="Account information">
        <form onSubmit={handleSaveName} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Full name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#221C51]"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Email</label>
            <input
              type="text"
              value={user.email}
              disabled
              className="w-full border border-gray-100 rounded-xl px-4 py-2.5 text-sm bg-gray-50 text-gray-400 cursor-not-allowed"
            />
            <p className="text-xs text-gray-400 mt-1">The email address cannot be changed.</p>
          </div>
          <button
            type="submit"
            disabled={savingProfile}
            className="bg-[#221C51] text-white text-sm px-6 py-2.5 rounded-xl hover:bg-[#2d2470] transition disabled:opacity-50"
          >
            {savingProfile ? 'Saving…' : 'Save'}
          </button>
        </form>
      </Section>

      {/* ── Security / Password ── */}
      <Section title="Security">
        {isGoogle ? (
          <div className="flex items-start gap-3 text-sm text-gray-500">
            <FaLock className="mt-0.5 text-gray-400 flex-shrink-0" />
            <p>Your account is linked to Google. Password management is handled through Google.</p>
          </div>
        ) : (
          <form onSubmit={handleSavePassword} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Current password</label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                autoComplete="current-password"
                className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#221C51]"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">New password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  autoComplete="new-password"
                  className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#221C51]"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Confirm</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                  className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#221C51]"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={savingPassword}
              className="bg-[#221C51] text-white text-sm px-6 py-2.5 rounded-xl hover:bg-[#2d2470] transition disabled:opacity-50"
            >
              {savingPassword ? 'Updating…' : 'Change password'}
            </button>
          </form>
        )}
      </Section>

      {/* ── Plan & Billing ── */}
      <Section title="My subscription">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isPremium ? 'bg-yellow-100' : 'bg-gray-100'}`}>
              <FaCrown className={isPremium ? 'text-yellow-500' : 'text-gray-400'} />
            </div>
            <div>
              <p className="font-semibold text-[#221C51] text-sm">{isPremium ? 'Premium' : 'Free plan'}</p>
              <p className="text-xs text-gray-400">{isPremium ? 'All features unlocked' : 'Upgrade to Premium to unlock everything'}</p>
            </div>
          </div>
          <Link
            to="/dashboard/billing"
            className="text-xs font-semibold text-[#221C51] border border-[#221C51] px-4 py-2 rounded-xl hover:bg-[#221C51] hover:text-white transition"
          >
            {isPremium ? 'Manage' : 'Go Premium'}
          </Link>
        </div>

        <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FaWallet className="text-[#221C51]" />
            <span className="text-sm text-gray-600">Ad budget (wallet)</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-lg font-black text-[#221C51]">€{walletBalance}</span>
            <Link to="/dashboard/billing" className="text-xs text-[#221C51] underline">Top up</Link>
          </div>
        </div>
      </Section>

      {/* ── Quick Links ── */}
      <Section title="Quick access">
        <div className="space-y-1">
          {[
            { to: '/dashboard/connected-platforms', icon: <FaLink />, label: 'Connected platforms', desc: 'Manage Meta, TikTok, Google…' },
            { to: '/dashboard/ads-ruleset', icon: <FaSlidersH />, label: 'Automated rules', desc: 'Configure your campaign rules' },
            { to: '/dashboard/billing', icon: <FaWallet />, label: 'Billing', desc: 'Subscription, wallet, history' },
            { to: '/dashboard/history', icon: <FaHistory />, label: 'Ad history', desc: 'See all campaigns across platforms' },
            { to: '/dashboard/ads-history', icon: <FaHistory />, label: 'Rule activity', desc: 'See your automated rule executions' },
          ].map(({ to, icon, label, desc }) => (
            <Link
              key={to}
              to={to}
              className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-gray-50 transition group"
            >
              <div className="w-8 h-8 rounded-lg bg-[#221C51]/10 flex items-center justify-center text-[#221C51] text-sm">
                {icon}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-[#221C51]">{label}</p>
                <p className="text-xs text-gray-400">{desc}</p>
              </div>
              <FaChevronRight className="text-gray-300 group-hover:text-[#221C51] transition text-xs" />
            </Link>
          ))}
        </div>
      </Section>
    </div>
  );
};

export default Profile;
